import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { debugAuth } from '../debug/debugLogger';
import logo from '../assets/logos/KUROCO_left corner_logo.png';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (!loading && user) navigate(from, { replace: true });
  }, [loading, user, navigate, from]);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('sessionExpired') === '1') {
        sessionStorage.removeItem('sessionExpired');
        setSessionExpired(true);
      }
    } catch {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      debugAuth('login_success', email.trim());
      // Do NOT navigate here — wait for onAuthStateChanged to update user, then useEffect will navigate.
      // Navigating immediately can race: ProtectedRoute may see user=null and redirect back to /login.
    } catch (err) {
      debugAuth('login_fail', err.message);
      setError(err.code === 'auth/invalid-credential' ? 'メールアドレスまたはパスワードが正しくありません。' : err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;
  if (user) return null;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 font-['Noto_Sans_JP',Helvetica,sans-serif] bg-[#E4E7E6]"
    >
      {/* Centered framed login card */}
      <div className="w-full max-w-[420px] rounded-xl shadow-lg p-8 md:p-10 bg-white border border-gray-200">
        {/* Logo + service name (AIMB — client request 2026-10-04) */}
        <div className="flex flex-col items-center mb-6">
          <div className="inline-flex px-5 py-3 bg-gray-50 rounded-lg">
            <img src={logo} alt="KUROCO" className="h-7 object-contain" />
          </div>
          <div className="mt-3 text-[#125D4F] text-2xl font-bold tracking-[0.2em]">AIMB</div>
        </div>

        <h1 className="text-[#1a1a1a] text-center text-lg font-semibold tracking-widest uppercase mb-2">
          ログイン
        </h1>
        <p className="text-gray-500 text-center text-sm mb-6">アカウント情報を入力してください</p>

        {sessionExpired && (
          <div className="mb-5 p-3 rounded-lg text-[13px] text-amber-800 bg-amber-50 border border-amber-200">
            セッションが切れました。再度ログインしてください。
          </div>
        )}
        {error && (
          <div className="mb-5 p-3 rounded-lg text-[13px] text-red-700 bg-red-50 border border-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="メールアドレス"
              required
              className="w-full px-4 py-3 text-sm rounded-lg outline-none border border-gray-300 bg-gray-50 text-[#1a1a1a] placeholder:text-gray-400 transition-colors focus:border-[#33564B] focus:bg-white"
            />
          </div>

          <div className="mb-4">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="パスワード"
                required
                className="w-full px-4 py-3 pr-11 text-sm rounded-lg outline-none border border-gray-300 bg-gray-50 text-[#1a1a1a] placeholder:text-gray-400 transition-colors focus:border-[#33564B] focus:bg-white"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-gray-500 hover:text-gray-700"
              >
                {showPassword ? '非表示' : '表示'}
              </button>
            </div>
          </div>

          <div className="flex justify-end mb-6">
            <Link
              to={email ? `/forgot-password?email=${encodeURIComponent(email)}` : '/forgot-password'}
              className="text-[13px] text-[#33564B] hover:underline font-medium"
            >
              パスワードをお忘れですか？
            </Link>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 text-[15px] font-semibold text-white rounded-lg uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-2 bg-[#33564B] hover:bg-[#2a463d]"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none" />
                  <path d="M8 2a6 6 0 0 1 6 6" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
                </svg>
                ログイン中...
              </>
            ) : (
              'ログイン'
            )}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-center text-sm text-gray-500">
            <Link to="/" className="text-[#33564B] hover:underline font-medium">
              ダッシュボードへ
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
